import React, { useCallback, useMemo } from 'react'
import { ChartView, CustomTrace, RechartData } from 'src/Libs/chart-kit/shared/utils/chartModel'
import type { PieChartData } from '../components/pie-chart.types'
import { getUniqueIdentifierFromCustomTrace } from 'src/Libs/system-charts/shared/utils/getUniqueIdentifierFromCustomTrace'
import { getChartColor } from 'src/Libs/system-charts/shared/config/chartColors'
import { isUndefined } from 'lodash'
import { getSumOfSelfProduction } from 'src/Libs/system-data-handler/utils/self-production'
import { Cell } from 'recharts'
import { getRechartDataSumValue } from '../utils/get-rechart-data-sum-value'
import { useStorageAutarkyValues } from 'src/Libs/storage/hooks'
import { StorageAutarky } from 'src/Libs/storage/models/storage.models'
import { formatNumberWithSIPrefix, STATISTICS_SI_PREFIXES } from 'src/Libs/system-data-handler/utils/formatNumberWithSIPrefix'

type UseChartDataAndCells = {
  data: PieChartData[]
  cells: React.ReactNode[]
  savingsSum: number
}

interface UseChartDataAndCellsProps {
  rechartData: RechartData
  customTraces?: CustomTrace[]
  impossibleTraces: string[]
  traceNames: Record<string, string>
  view: ChartView
  isDynamicPowerPriceEnabled: boolean /* this is a temporary condition added with AF-3237. The plan is to remove it after SW1 and SW2 align. */
  isCostSavingsPieChart?: boolean
  isLiveView?: boolean
  isAutarkyPieChart?: boolean
  inactiveView?: boolean
}

/*
 * This hook calculates the data and cells for the pie chart
 */
export const useChartDataAndCells = ({
  rechartData,
  customTraces,
  impossibleTraces,
  traceNames,
  view,
  isDynamicPowerPriceEnabled,
  isCostSavingsPieChart,
  isLiveView = false,
  isAutarkyPieChart,
  inactiveView,
}: UseChartDataAndCellsProps): UseChartDataAndCells => {
  const savingsSum = getRechartDataSumValue(rechartData)
  const { data: storageAutarkyData } = useStorageAutarkyValues(isAutarkyPieChart)

  const getAutarkyValue = useCallback(
    (key: string): number | null => {
      if (!storageAutarkyData) return null

      const mappedKey = AUTARKY_KEY_MAP[key]

      return storageAutarkyData[mappedKey] ?? null
    },
    [storageAutarkyData]
  )

  const { data, cells } = useMemo(() => {
    const data: PieChartData[] = []
    const cells: React.ReactNode[] = []

    if (!rechartData?.sums || !customTraces || inactiveView) return { data, cells }

    for (const customTrace of customTraces) {
      if (impossibleTraces.includes(customTrace.key)) continue
      if (!(customTrace.key in rechartData.sums)) continue

      const valueSum = calculateValueSum(isDynamicPowerPriceEnabled, customTrace, rechartData, view, isLiveView)
      let value: number | null = null

      if (isAutarkyPieChart) {
        value = getAutarkyValue(customTrace.key)
      } else if (isCostSavingsPieChart) {
        value = valueSum
      } else if (isLiveView) {
        value = Math.round(valueSum ?? 0)
      } else {
        const [formattedValue] = formatNumberWithSIPrefix(valueSum ?? 0, 1, undefined, STATISTICS_SI_PREFIXES)
        value = formattedValue === 0 ? 0 : valueSum
      }

      fillPieChartData(data, customTrace, traceNames, value)
      generateCells(cells, customTrace, value, savingsSum)
    }

    return { data, cells }
  }, [
    getAutarkyValue,
    isAutarkyPieChart,
    rechartData,
    customTraces,
    impossibleTraces,
    isDynamicPowerPriceEnabled,
    view,
    isCostSavingsPieChart,
    traceNames,
    savingsSum,
    isLiveView,
    inactiveView,
  ])

  return { data, cells, savingsSum }
}

/**
 * Fills pie chart data
 */
const fillPieChartData = (data: PieChartData[], customTrace: CustomTrace, traceNames: Record<string, string>, value: number | null): void => {
  const name = customTrace.key in traceNames ? traceNames[customTrace.key] : '???'
  data.push({
    name,
    value: value,
    color: customTrace.color,
    key: customTrace.key,
  })
}

/**
 * Fill the pie chart cells
 */
const generateCells = (cells: React.ReactNode[], customTrace: CustomTrace, value: number | null, sum: number): void => {
  const key = getUniqueIdentifierFromCustomTrace(customTrace)
  const doNotDisplay = value === null || value <= 0 || sum <= 0

  if (doNotDisplay) return

  const cellFill = customTrace.color ?? getChartColor(customTrace.key)
  cells.push(<Cell key={key} fill={cellFill} stroke={cellFill} />)
}

/**
 * Calculates the sum of values for a given trace
 */
const calculateValueSum = (
  isDynamicPowerPriceEnabled: boolean,
  customTrace: CustomTrace,
  rechartData: RechartData,
  view: ChartView,
  isLiveView = false
): number | null => {
  if (isUndefined(rechartData?.sums)) return null

  if (isDynamicPowerPriceEnabled && !isLiveView && (customTrace.key === 'SelfProduction' || customTrace.key === 'Autarky')) {
    return getSumOfSelfProduction(rechartData.data, view)
  }
  return rechartData.sums?.[customTrace.key].history
}

const AUTARKY_KEY_MAP: Record<string, keyof Pick<StorageAutarky, 'autarkyPercentage' | 'gridImport360Percentage' | 'gridImportPercentage'>> = {
  Autarky: 'autarkyPercentage',
  ThreeSixtySavings: 'gridImport360Percentage',
  NetOutWithoutThreeSixtySavings: 'gridImportPercentage',
  NetOut: 'gridImportPercentage',
}
